import React from 'react'
import type { DashboardMetrics } from '../../hooks/useDashboard'
import { Card } from '../ui/Card'

interface TopRestaurantsListProps {
  restaurants: DashboardMetrics['top_restaurants']
  loading?: boolean
}

export const TopRestaurantsList: React.FC<TopRestaurantsListProps> = ({ restaurants, loading }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatTime = (minutes: number) => {
    return `${Math.round(minutes)}m`
  }

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-48"></div>
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </Card>
    )
  }

  if (!restaurants || restaurants.length === 0) {
    return (
      <Card className="p-6">
        <h3 className="text-xl font-bold text-heading mb-4">📊 Top 5 Restaurants Today</h3>
        <p className="text-muted text-center py-8">No restaurant data available</p>
      </Card>
    )
  }

  return (
    <Card className="p-6">
      <h3 className="text-xl font-bold text-heading mb-4">📊 Top 5 Restaurants Today</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2 text-xs font-semibold text-muted uppercase">Rank</th>
              <th className="text-left py-3 px-2 text-xs font-semibold text-muted uppercase">Restaurant</th>
              <th className="text-right py-3 px-2 text-xs font-semibold text-muted uppercase">Orders</th>
              <th className="text-right py-3 px-2 text-xs font-semibold text-muted uppercase">Revenue</th>
              <th className="text-right py-3 px-2 text-xs font-semibold text-muted uppercase">Avg Time</th>
              <th className="text-right py-3 px-2 text-xs font-semibold text-muted uppercase">Cancel %</th>
            </tr>
          </thead>
          <tbody>
            {restaurants.map((restaurant) => (
              <tr
                key={restaurant.id}
                className="border-b border-border"
              >
                <td className="py-3 px-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    restaurant.rank === 1 ? 'bg-yellow-100 text-yellow-700' :
                    restaurant.rank === 2 ? 'bg-gray-100 text-gray-700' :
                    restaurant.rank === 3 ? 'bg-orange-100 text-orange-700' :
                    'bg-blue-50 text-blue-700'
                  }`}>
                    {restaurant.rank === 1 ? '🥇' :
                     restaurant.rank === 2 ? '🥈' :
                     restaurant.rank === 3 ? '🥉' :
                     restaurant.rank}
                  </div>
                </td>
                <td className="py-3 px-2">
                  <p className="font-semibold text-heading">{restaurant.name}</p>
                </td>
                <td className="py-3 px-2 text-right">
                  <span className="font-semibold text-heading">{restaurant.orders}</span>
                </td>
                <td className="py-3 px-2 text-right">
                  <span className="font-semibold text-success-600">{formatCurrency(restaurant.revenue)}</span>
                </td>
                <td className="py-3 px-2 text-right">
                  <span className="text-sm text-muted">{formatTime(restaurant.avg_fulfillment_time)}</span>
                </td>
                <td className="py-3 px-2 text-right">
                  <span className={`text-sm font-medium ${
                    restaurant.cancel_rate > 20 ? 'text-danger-600' :
                    restaurant.cancel_rate > 10 ? 'text-warning-600' :
                    'text-success-600'
                  }`}>
                    {restaurant.cancel_rate.toFixed(1)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
